import * as moment from 'moment';

import { Component, Input, OnInit } from '@angular/core';

import {
  EventPricingMatrix,
  EventType,
  NonRepeatingEvent
} from '../../../../../../../models/event-type';

import { AccountService } from '../../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';
import {
  dateStringFromISO8601,
  dayOfWeekStringFromISO8601,
  timeStringFromISO8601TimeOnly
} from '../../../../../../../utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CancelEventModalComponent } from '../../../modals/cancel-event-modal/cancel-event-modal.component';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { DuplicateOccurrenceModalComponent } from './modals/duplicate-occurrence-modal/duplicate-occurrence-modal.component';
import { CapitanLocation } from '../../../../../../../models/location';

@Component({
  selector: 'app-event-type-schedules-non-repeating',
  templateUrl: './event-type-schedules-non-repeating.component.html',
  styleUrls: ['./event-type-schedules-non-repeating.component.css']
})
export class EventTypeSchedulesNonRepeatingComponent implements OnInit {

  readonly SUBMIT_TYPE_GO_BACK = 'goBack';
  readonly SUBMIT_TYPE_CONTINUE = 'continue';

  @Input()
  eventType: EventType;

  @Input()
  pricingMatrices: EventPricingMatrix[];

  @Input()
  locations: CapitanLocation[];

  // null indicates "all locations"
  public locationId: number | null = null;
  public hideEnded = true;

  public events: NonRepeatingEvent[];

  public successMessage = '';

  public loadingEventsList = true;

  public eventsNotFound = false;
  public serverError: number = null;

  public dateStringFromISO8601 = dateStringFromISO8601;
  public dayOfWeekStringFromISO8601 = dayOfWeekStringFromISO8601;
  public timeStringFromISO8601TimeOnly = timeStringFromISO8601TimeOnly;

  constructor(
    private accountService: AccountService,
    public organizationConfigService: OrganizationConfigService,
    private eventTypeService: EventTypeService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.loadEvents();
  }

  /**
   * Whether there are any outstanding API requests in progress.
   */
  public isLoading() {
    return (
      this.loadingEventsList
    );
  }

  /**
   * Remove/cancel the given event.
   */
  public submitRemoveEvent(event: NonRepeatingEvent) {
    this.successMessage = '';

    // If the event has a positive ID, then it is present in the API and must
    // be cancelled.
    if (event.id > 0) {
      const modalRef = this.modalService.open(CancelEventModalComponent);
      modalRef.componentInstance.eventId = event.id;
      modalRef.componentInstance.savedCallback = () => {
        this.removeEvent(event);
        this.successMessage = 'Occurrence removed successfully.';
      };
    } else {
      this.removeEvent(event);
      this.successMessage = 'Occurrence removed successfully.';
    }
  }

  public submitDuplicateEvent(event: NonRepeatingEvent) {
    this.successMessage = '';

    const modalRef = this.modalService.open(DuplicateOccurrenceModalComponent);
    modalRef.componentInstance.occurrence = event;
    modalRef.componentInstance.savedCallback = () => {
      this.successMessage = 'Occurrence duplicated successfully.';
      this.loadEvents();
    };
  }

  public getPricingMatrixNameById(pricingMatrixId: number) {
    for (const pricingMatrix of this.pricingMatrices) {
      if (pricingMatrix.id === pricingMatrixId) {
        return pricingMatrix.name;
      }
    }
    return 'Matrix Not Found';
  }

  public getFilteredEvents(): NonRepeatingEvent[] {
    if (!this.events) { return this.events; }

    return this.events.filter(event => {
      if (this.locationId) {
        if (event.location_id !== this.locationId) { return false; }
      }

      if (this.hideEnded) {
        let hasNonEndedPart = false;
        for (const ep of event.event_parts) {
          if (moment(ep.end_datetime_isoformat).isAfter(moment())) {
            hasNonEndedPart = true;
          }
        }
        if (!hasNonEndedPart) { return false; }
      }

      return true;
    })
  }

  /**
   * Load the events for the given event type ID and selected location.
   */
  private loadEvents() {
    this.loadingEventsList = true;

    this.eventTypeService
      .getNonRepeatingEvents(this.eventType.id, null, false)
      .then(response => {
        this.events = response;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventsNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventsList = false; });
  }

  /**
   * Remove the given event from the list.
   */
  private removeEvent(event: NonRepeatingEvent) {
    this.events.splice(this.events.indexOf(event), 1);
  }

}
